import React from 'react';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import StatCard from '../components/StatCard';
import ChartBlock from '../components/ChartBlock';
import DonutChart from '../components/DonutChart';
import RankList from '../components/RankList';
import styles from '../components/stats.module.css';
import palette from '../components/palette';
import { formatMoney, formatPercent } from '../utils/format';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const PREVIEW_HELP = 'Prévia pelo valor de tabela de cada operadora. O que a operadora de fato pagou ainda não é registrado.';

/** Quantos atendimentos e, quando houver, quantos ainda sem valor de tabela (não entram na soma). */
function countHint(count, unpriced, one, many) {
  const text = plural(count, one, many);
  return unpriced ? `${text}, ${unpriced} sem valor de tabela` : text;
}

/**
 * Bloco "Particular x Convênio" da seção Financeiro: quanto da agenda é por
 * convênio, a prévia do que as operadoras devem (valor de tabela) e a
 * coparticipação já recebida. Só aparece para quem atende convênio.
 */
const InsuranceStats = ({ insurance }) => {
  if (!insurance || !insurance.used) return null;
  const { mix } = insurance;
  const total = mix.private + mix.insurance;

  const cards = [
    {
      key: 'insuranceCount', label: 'Atendimentos por convênio', icon: <HealthAndSafetyOutlinedIcon />, accent: 'teal',
      hint: total > 0 ? `${formatPercent((mix.insurance / total) * 100)} da agenda` : '',
    },
    {
      key: 'receivable', label: 'A receber das operadoras', icon: <RequestQuoteOutlinedIcon />, type: 'money', accent: 'amber',
      hint: countHint(insurance.receivableCount, insurance.receivableUnpriced, 'atendimento realizado', 'atendimentos realizados'),
      help: PREVIEW_HELP,
    },
    {
      key: 'forecast', label: 'Previsto das operadoras', icon: <EventNoteOutlinedIcon />, type: 'money', accent: 'blue',
      hint: countHint(insurance.forecastCount, insurance.forecastUnpriced, 'atendimento por vir', 'atendimentos por vir'),
      help: PREVIEW_HELP,
    },
    {
      key: 'copay', label: 'Coparticipação recebida', icon: <PaymentsOutlinedIcon />, type: 'money', accent: 'green',
      hint: plural(insurance.copayCount, 'pagamento', 'pagamentos'),
    },
  ];

  return (
    <>
      <span className={styles.subTitle}>Convênios (prévia)</span>
      <div className={styles.grid}>
        {cards.map((card) => <StatCard key={card.key} {...card} kpi={insurance.kpis[card.key]} />)}
      </div>

      <div className={styles.rowEven}>
        <ChartBlock title="Particular x Convênio" hint="atendimentos">
          <DonutChart
            items={[
              { label: 'Particular', value: mix.private, color: palette.blue },
              { label: 'Convênio', value: mix.insurance, color: palette.teal },
            ]}
            centerValue={total}
            centerLabel="atendimentos"
            empty="Nenhum atendimento no período."
          />
        </ChartBlock>
        <ChartBlock title="A receber por operadora" hint="prévia">
          <RankList
            items={insurance.byProvider.map((item) => ({
              label: item.label,
              value: item.amount,
              display: formatMoney(item.amount),
              sub: plural(item.count, 'atendimento', 'atendimentos'),
            }))}
            color={palette.teal}
            empty="Nenhum atendimento por convênio realizado no período."
          />
        </ChartBlock>
      </div>
    </>
  );
};

export default InsuranceStats;
